import React, { useEffect, useState } from 'react'
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Select,
  Upload,
  Divider,
  Space,
  Table,
  Modal,
  Spin,
} from 'antd'
import {
  UploadOutlined,
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import AdminHeader from '../CommonComponents/AdminHeader'
import AdminFooter from '../CommonComponents/AdminFooter'
import './StudentAdmissionForm.css'
import { toast } from 'react-toastify'
import {
  DeleteParentsDocuments,
  getChildrenList,
  GetParentsDocuments,
  GetParentsDocumentsById,
  IMG_URL,
  saveParentsDocuments,
  UpdateParentsDocuments,
} from '../APIComponents/AdminApi'
import axiosInstance from '../../Utils/axiosInstance'

const { Option } = Select

const ParentsDocumentForm = () => {
  //===============================================
  const loginType = sessionStorage.getItem('loginType') // 'ADMIN' or 'USER'
  const menuData = JSON.parse(sessionStorage.getItem('menus')) || []

  const getPagePermission = () => {
    if (loginType === 'ADMIN') {
      return {
        is_new: 1,
        is_edit: 1,
        is_view: 1,
        is_delete: 1,
      }
    }

    for (const menu of menuData) {
      const page = menu.pages?.find(
        (p) => p.page_name === 'Parents Info/Documents',
      )
      if (page) return page
    }

    return {
      is_new: 0,
      is_edit: 0,
      is_view: 0,
      is_delete: 0,
    }
  }

  const pagePermission = getPagePermission()
  const showActionColumn =
    loginType === 'ADMIN' ||
    pagePermission.is_edit == 1 ||
    pagePermission.is_view == 1 ||
    pagePermission.is_delete == 1
  //============================================

  const [form] = Form.useForm()

  const [childrenList, setChildrenList] = useState([])
  const [tableData, setTableData] = useState([])
  const [loading, setLoading] = useState(false)

  const [showForm, setShowForm] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [recordId, setRecordId] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const [editLoading, setEditLoading] = useState(false)
  const [tableLoading, setTableLoading] = useState(false)
  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [viewData, setViewData] = useState(null)

  const student = viewData?.info?.student

  const handleView = async (record) => {
    try {
      const res = await axiosInstance.get(
        `${GetParentsDocumentsById}/${record.reg_id}`,
      )

      const { parentInfo, parentDoc } = res.data

      setViewData({
        info: parentInfo?.[0],
        docs: parentDoc?.[0],
      })

      setViewModalVisible(true)
    } catch (error) {
      toast.error('Failed to fetch details')
    }
  }

  useEffect(() => {
    fetchChildren()
    fetchTableData()
  }, [])

  // ================= FETCH CHILDREN =================
  const fetchChildren = async () => {
    try {
      const res = await axiosInstance.get(getChildrenList)
      setChildrenList(res.data)
    } catch (err) {
      toast.error('Failed to fetch children list')
    }
  }

  // ================= FETCH TABLE DATA =================

  const fetchTableData = async () => {
    try {
      setTableLoading(true)
      const res = await axiosInstance.get(GetParentsDocuments)

      const data = res.data

      // If API returns single object with parentInfo & parentDoc
      if (data?.parentInfo && Array.isArray(data.parentInfo)) {
        const mergedData = data.parentInfo.map((info) => {
          const doc = data.parentDoc?.find((d) => d.reg_id === info.reg_id)

          return {
            ...info,
            parentDoc: doc || {},
            student: info.student,
          }
        })

        setTableData(mergedData)
      } else {
        setTableData([])
      }
    } catch (err) {
      toast.error('Failed to fetch table data')
    } finally {
      setTableLoading(false)
    }
  }

  // ================= ADD =================
  const handleAdd = () => {
    form.resetFields()
    setIsEdit(false)
    setRecordId(null)
    setShowForm(true)
  }

  // ================= EDIT =================
  const handleEdit = async (record) => {
    try {
      setEditLoading(true)
      const res = await axiosInstance.get(
        `${GetParentsDocumentsById}/${record.reg_id}`,
      )

      const { parentInfo, parentDoc } = res.data
      const info = parentInfo?.[0]
      const docs = parentDoc?.[0] || {}

      form.setFieldsValue({
        childName: record.reg_id,
        fatherName: info?.father_full_name,
        motherName: info?.mother_full_name,
        fatherOccupation: info?.father_occupation,
        motherOccupation: info?.mother_occupation,
        annualIncome: info?.family_annual_income,
        familyMembers: info?.family_members,
        address: info?.address,

        // FATHER DOCUMENT TYPE
        fatherDocType: docs?.father_document_type,

        // FATHER DOCUMENT FILE
        fatherDocument: docs?.father_document
          ? [
              {
                uid: '-1',
                name: docs.father_document,
                status: 'done',
                url: IMG_URL + docs.father_document,
              },
            ]
          : [],

        // MOTHER DOCUMENT TYPE
        motherDocType: docs?.mother_document_type,

        // MOTHER DOCUMENT FILE
        motherDocument: docs?.mother_document
          ? [
              {
                uid: '-2',
                name: docs.mother_document,
                status: 'done',
                url: IMG_URL + docs.mother_document,
              },
            ]
          : [],
      })

      setIsEdit(true)
      setRecordId(info.id)
      setShowForm(true)
    } catch (error) {
      console.error(error)
    } finally {
      setEditLoading(false)
    }
  }

  // ================= DELETE =================
  const handleDelete = async () => {
    try {
      const res = await axiosInstance.delete(
        `${DeleteParentsDocuments}/${deleteId}`,
      )
      if (res.data === 'DELETED') {
        toast.success('Deleted successfully')
        setDeleteModal(false)
        fetchTableData()
      } else if (res.data === 'NOTFOUND') {
        toast.warn('Data not found!')
      }
    } catch (err) {
      toast.error('Delete failed')
    }
  }

  // ================= CANCEL =================
  const handleCancel = () => {
    form.resetFields()
    setIsEdit(false)
    setRecordId(null)
    setShowForm(false)
  }

  // ================= SUBMIT / UPDATE =================
  const handleFinish = async (values) => {
    try {
      setLoading(true)

      const formData = new FormData()

      formData.append('reg_id', values.childName)
      formData.append('father_full_name', values.fatherName || '')
      formData.append('mother_full_name', values.motherName || '')
      formData.append('father_occupation', values.fatherOccupation || '')
      formData.append('mother_occupation', values.motherOccupation || '')
      formData.append('family_annual_income', values.annualIncome || '')
      formData.append('family_members', values.familyMembers || '')
      formData.append('address', values.address || '')

      const appendFile = (field, fileList) => {
        if (!fileList || fileList.length === 0) return

        const file = fileList[0]

        if (file.originFileObj) {
          formData.append(field, file.originFileObj)
        } else if (file.url) {
          const existingPath = file.url.replace(IMG_URL, '')
          formData.append(field, existingPath)
        }
      }

      formData.append('father_document_type', values.fatherDocType)
      formData.append('mother_document_type', values.motherDocType)

      appendFile('father_document', values.fatherDocument)
      appendFile('mother_document', values.motherDocument)

      let res

      if (isEdit) {
        res = await axiosInstance.put(
          `${UpdateParentsDocuments}/${recordId}`,
          formData,
        )

        if (res.data === 'UPDATED') {
          toast.success('Updated Successfully!')
          form.resetFields()
          setIsEdit(false)
          setRecordId(null)
          setShowForm(false)
          fetchTableData()
        }
      } else {
        res = await axiosInstance.post(saveParentsDocuments, formData)

        if (res.data === 'SAVED') {
          toast.success('Saved Successfully!')
          form.resetFields()
          setIsEdit(false)
          setRecordId(null)
          setShowForm(false)
          fetchTableData()
        } else if (res.data === 'ALREADY_EXIST') {
          toast.warn('Data already exist!')
        }
      }
    } catch (err) {
      toast.error('Failed to submit data')
    } finally {
      setLoading(false)
    }
  }

  // ================= FILE VALIDATION =================
  const validateFile = (file) => {
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/pdf',
    ]

    const isAllowedType = allowedTypes.includes(file.type)

    if (!isAllowedType) {
      toast.error('Only JPG, PNG, and PDF files are allowed!')
      return Upload.LIST_IGNORE
    }

    const isLt3M = file.size / 1024 / 1024 < 3

    if (!isLt3M) {
      toast.warn('File must be smaller than 3MB!')
      return Upload.LIST_IGNORE
    }

    return false // prevent auto upload
  }
  // ================= TABLE COLUMNS =================
  const columns = [
    {
      title: 'Sr No',
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: 'Student Name',
      render: (_, record) =>
        `${record?.student?.fname || ''} ${record?.student?.mname || ''} ${
          record?.student?.lname || ''
        }`,
    },
    {
      title: 'Father Name',
      dataIndex: 'father_full_name',
    },
    {
      title: 'Mother Name',
      dataIndex: 'mother_full_name',
    },
    {
      title: 'Annual Income',
      dataIndex: 'family_annual_income',
    },
    ...(showActionColumn
      ? [
          {
            title: 'Action',
            render: (_, record) => (
              <Space>
                {pagePermission.is_view == 1 && (
                  <Button
                    title="View"
                    icon={<EyeOutlined />}
                    onClick={() => handleView(record)}
                  />
                )}
                {pagePermission.is_edit == 1 && (
                  <Button
                    title="Edit"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  />
                )}
                {pagePermission.is_delete == 1 && (
                  <Button
                    title="Delete"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setDeleteId(record.reg_id)
                      setDeleteModal(true)
                    }}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ]

  const documentOptions = [
    {
      id: 1,
      name: 'Voter ID',
    },
    {
      id: 2,
      name: 'Passport',
    },
    {
      id: 3,
      name: 'Ration Card',
    },
    {
      id: 4,
      name: 'Driving Licence',
    },
  ]

  return (
    <>
      <AdminHeader />

      <div className="student-form-section container-fluid">
        <div className="student-form-wrapper pb-5">
          <div className="row student-form-title">
            <h3 className="section-title1 text-start mb-3">
              Family Info & Parents Documents
            </h3>
          </div>

          {/* ================= TABLE SECTION ================= */}
          {!showForm && (
            <>
              <Row justify="space-between" className="mb-3">
                <Col xs={24} md={10} lg={14}></Col>
                <Col
                  xs={24}
                  md={6}
                  lg={4}
                  className="text-end px-md-2 pb-2 pb-md-0"
                >
                  {pagePermission.is_new == 1 && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleAdd}
                    >
                      Add
                    </Button>
                  )}
                </Col>

                <Col xs={24} md={6} lg={6}>
                  <Input
                    placeholder="Search by name"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </Col>
              </Row>

              <Table
                dataSource={tableData.filter((item) => {
                  const search = searchText.toLowerCase()

                  return (
                    item?.student?.fname?.toLowerCase().includes(search) ||
                    item?.student?.mname?.toLowerCase().includes(search) ||
                    item?.student?.lname?.toLowerCase().includes(search) ||
                    item?.father_full_name?.toLowerCase().includes(search) ||
                    item?.mother_full_name?.toLowerCase().includes(search)
                  )
                })}
                columns={columns}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  onChange: (page, size) => {
                    setCurrentPage(page)
                    setPageSize(size)
                  },
                }}
                rowKey="id"
                bordered
                loading={tableLoading}
                className="shadow rounded custom-table p-0"
                style={{
                  overflowX: 'auto',
                  border: '1px solid #dfdfdf',
                  backgroundColor: 'white',
                }}
              />
            </>
          )}

          {/* ================= FORM SECTION ================= */}
          {showForm && (
            <Spin spinning={editLoading}>
              <div className="row student-form-container rounded shadow">
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleFinish}
                  autoComplete="off"
                >
                  {/* Child Selection */}
                  <Row
                    gutter={16}
                    className="mt-3 p-2 border rounded"
                    style={{ background: '#fafafa' }}
                  >
                    <Col xs={24} md={8}>
                      <Form.Item
                        label="Select Children Name"
                        name="childName"
                        rules={[
                          { required: true, message: 'Please select a child' },
                        ]}
                      >
                        <Select
                          showSearch
                          disabled={isEdit}
                          placeholder="Search by name / reg no"
                          filterOption={(input, option) => {
                            const child = childrenList.find(
                              (c) => c.id === option.value,
                            )
                            const searchText = `
                            ${child?.fname}
                            ${child?.mname}
                            ${child?.lname}
                            ${child?.reg_no}
                          `.toLowerCase()
                            return searchText.includes(input.toLowerCase())
                          }}
                        >
                          {childrenList.map((child) => (
                            <Option key={child.id} value={child.id}>
                              {child.fname} {child.mname} {child.lname} (Reg No:{' '}
                              {child.reg_no})
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Parent Info */}
                  <Row
                    gutter={16}
                    className="p-2 border rounded mt-3"
                    style={{ background: '#fafafa' }}
                  >
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Father's Full Name"
                        name="fatherName"
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Mother's Full Name"
                        name="motherName"
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12} lg={6}>
                      <Form.Item
                        label="Father's Occupation"
                        name="fatherOccupation"
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12} lg={6}>
                      <Form.Item
                        label="Mother's Occupation"
                        name="motherOccupation"
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12} lg={6}>
                      <Form.Item
                        label="Annual Income"
                        name="annualIncome"
                        rules={[{ required: true }]}
                      >
                        <Input type="number" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12} lg={6}>
                      <Form.Item
                        label="Family Members"
                        name="familyMembers"
                        rules={[{ required: true }]}
                      >
                        <Input type="number" />
                      </Form.Item>
                    </Col>

                    <Col xs={24}>
                      <Form.Item
                        label="Address"
                        name="address"
                        rules={[{ required: true }]}
                      >
                        <Input.TextArea rows={3} />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Documents */}
                  {/* <Row
                    gutter={16}
                    className="p-2 border rounded mt-3"
                    style={{ background: '#fafafa' }}
                  >
                    {[
                      { label: "Father's Aadhar Card", name: 'fatherAadhar' },
                      { label: "Mother's Aadhar Card", name: 'motherAadhar' },
                      {
                        label: 'Income Certificate',
                        name: 'incomeCertificate',
                      },
                      { label: 'Ration Card', name: 'rationCard' },
                    ].map((item) => (
                      <Col xs={24} md={12} lg={6} key={item.name}>
                        <Form.Item
                          label={item.label}
                          name={item.name}
                          valuePropName="fileList"
                          getValueFromEvent={(e) =>
                            Array.isArray(e) ? e : e?.fileList
                          }
                          rules={[{ required: true }]}
                        >
                          <Upload
                            beforeUpload={validateFile}
                            accept=".jpg,.jpeg,.png,.pdf"
                            maxCount={1}
                            listType="picture"
                            style={{ width: '100%' }}
                          >
                            <Button
                              icon={<UploadOutlined />}
                              style={{ width: '100%' }}
                            >
                              Upload (Max 3MB)
                            </Button>
                          </Upload>
                        </Form.Item>
                      </Col>
                    ))}
                  </Row> */}

                  <Row
                    gutter={16}
                    className="p-2 border rounded mt-3"
                    style={{ background: '#fafafa' }}
                  >
                    {/* Father Document */}
                    <Col xs={24} md={6}>
                      <Form.Item
                        label="Father Document"
                        name="fatherDocType"
                        rules={[{ required: true, message: 'Select document' }]}
                      >
                        <Select placeholder="Select Document">
                          {documentOptions.map((doc) => (
                            <Option key={doc.id} value={doc.id}>
                              {doc.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={6}>
                      <Form.Item
                        shouldUpdate={(prev, curr) =>
                          prev.fatherDocType !== curr.fatherDocType
                        }
                      >
                        {({ getFieldValue }) =>
                          getFieldValue('fatherDocType') && (
                            <Form.Item
                              label=""
                              className="mt-4 pt-2"
                              name="fatherDocument"
                              valuePropName="fileList"
                              getValueFromEvent={(e) =>
                                Array.isArray(e) ? e : e?.fileList
                              }
                              rules={[{ required: true }]}
                            >
                              <Upload
                                beforeUpload={validateFile}
                                accept=".jpg,.jpeg,.png,.pdf"
                                maxCount={1}
                                style={{ width: '100%' }}
                              >
                                <Button
                                  icon={<UploadOutlined />}
                                  style={{ width: '100%' }}
                                >
                                  Upload
                                </Button>
                              </Upload>
                            </Form.Item>
                          )
                        }
                      </Form.Item>
                    </Col>

                    {/* Mother Document */}
                    <Col xs={24} md={6}>
                      <Form.Item
                        label="Mother Document"
                        name="motherDocType"
                        rules={[{ required: true, message: 'Select document' }]}
                      >
                        <Select placeholder="Select Document">
                          {documentOptions.map((doc) => (
                            <Option key={doc.id} value={doc.id}>
                              {doc.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={6}>
                      <Form.Item
                        shouldUpdate={(prev, curr) =>
                          prev.motherDocType !== curr.motherDocType
                        }
                      >
                        {({ getFieldValue }) =>
                          getFieldValue('motherDocType') && (
                            <Form.Item
                              label=""
                              className="mt-4 pt-2"
                              name="motherDocument"
                              valuePropName="fileList"
                              getValueFromEvent={(e) =>
                                Array.isArray(e) ? e : e?.fileList
                              }
                              rules={[{ required: true }]}
                            >
                              <Upload
                                beforeUpload={validateFile}
                                accept=".jpg,.jpeg,.png,.pdf"
                                maxCount={1}
                                style={{ width: '100%' }}
                              >
                                <Button
                                  icon={<UploadOutlined />}
                                  style={{ width: '100%' }}
                                >
                                  Upload
                                </Button>
                              </Upload>
                            </Form.Item>
                          )
                        }
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider />

                  <Row justify="end" className="pb-5">
                    <Space>
                      <Button onClick={handleCancel}>Cancel</Button>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                      >
                        {isEdit ? 'Update' : 'Submit'}
                      </Button>
                    </Space>
                  </Row>
                </Form>
              </div>
            </Spin>
          )}

          {/* ===== DELETE MODAL ===== */}
          <Modal
            title="Confirm Delete"
            open={deleteModal}
            onOk={handleDelete}
            onCancel={() => setDeleteModal(false)}
          >
            Are you sure you want to delete this record?
          </Modal>

          {/* ===== VIEW MODAL ===== */}
          <Modal
            title={
              <div style={{ fontSize: '18px', fontWeight: 600 }}>
                👁 View Family Info & Parents Documents
              </div>
            }
            open={viewModalVisible}
            onCancel={() => setViewModalVisible(false)}
            footer={[
              <Button
                key="close"
                type="primary"
                onClick={() => setViewModalVisible(false)}
              >
                Close
              </Button>,
            ]}
            width={900}
            style={{
              top: 20, // space from top
              paddingBottom: 50, // space from bottom
            }}
            centered
            bodyStyle={{
              maxHeight: '75vh', // control height
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingBottom: 10,
            }}
          >
            {viewData && (
              <div
                style={{
                  backgroundColor: '#f5f7fa',
                  padding: '20px 5px',
                  borderRadius: '8px',
                }}
              >
                {/* Student Info */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Student Name :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData?.info?.student?.fname || ''}{' '}
                      {viewData?.info?.student?.mname || ''}{' '}
                      {viewData?.info?.student?.lname || ''}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Father Name :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData?.info?.father_full_name}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Mother Name :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData?.info?.mother_full_name}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Father Occupation :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData?.info?.father_occupation}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Mother Occupation :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData?.info?.mother_occupation}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Annual Income :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData?.info?.family_annual_income}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Family Members :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData?.info?.family_members}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Address :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">{viewData?.info?.address}</div>
                  </Col>
                </Row>

                <Divider />

                {/* Documents Section */}
                <Row gutter={16}>
                  {viewData?.docs?.father_aadhar_card && (
                    <Col span={12} className="py-2">
                      <div className="file-card">
                        <strong>Father Aadhar Card</strong>
                        <div style={{ marginTop: 10 }}>
                          <a
                            href={IMG_URL + viewData.docs.father_aadhar_card}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View File
                          </a>
                        </div>
                      </div>
                    </Col>
                  )}

                  {viewData?.docs?.mother_aadhar_card && (
                    <Col span={12} className="py-2">
                      <div className="file-card">
                        <strong>Mother Aadhar Card</strong>
                        <div style={{ marginTop: 10 }}>
                          <a
                            href={IMG_URL + viewData.docs.mother_aadhar_card}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View File
                          </a>
                        </div>
                      </div>
                    </Col>
                  )}

                  {viewData?.docs?.income_certificate && (
                    <Col span={12} className="py-2">
                      <div className="file-card">
                        <strong>Income Certificate</strong>
                        <div style={{ marginTop: 10 }}>
                          <a
                            href={IMG_URL + viewData.docs.income_certificate}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View File
                          </a>
                        </div>
                      </div>
                    </Col>
                  )}

                  {viewData?.docs?.ration_card && (
                    <Col span={12} className="py-2">
                      <div className="file-card">
                        <strong>Ration Card</strong>
                        <div style={{ marginTop: 10 }}>
                          <a
                            href={IMG_URL + viewData.docs.ration_card}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View File
                          </a>
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              </div>
            )}
          </Modal>
        </div>
      </div>

      <AdminFooter />
    </>
  )
}

export default ParentsDocumentForm
